/**
 * Utilities for handling API errors, especially when the backend does not respond
 * (network failures, timeouts) such as IIS cold starts.
 */

import { parseApiResponse, getErrorMessage } from './responseModel';

export const NO_RESPONSE_RETRY_COUNT = 3;
export const NO_RESPONSE_RETRY_DELAY_MS = 2000;

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * True when the request failed before receiving any HTTP response.
 */
export const isNoResponseError = (error) => {
    if (!error || error.response) {
        return false;
    }

    if (error.code === 'ERR_CANCELED') {
        return false;
    }

    return true;
};

/**
 * Error messages that should never be shown to end users.
 */
export const isSuppressedUserErrorMessage = (message) => {
    if (!message || typeof message !== 'string') {
        return false;
    }

    const normalized = message.toLowerCase();

    return (
        normalized === 'network error' ||
        normalized.includes('network error') ||
        normalized.includes('timeout') ||
        normalized.includes('exceeded') ||
        normalized === 'err_network' ||
        normalized.includes('econnaborted') ||
        normalized.includes('econnrefused') ||
        normalized.includes('econnreset')
    );
};

/**
 * Whether this error should be hidden from user-facing notifications.
 */
export const shouldSuppressUserError = (error) => {
    if (!error) {
        return false;
    }

    if (error.suppressUserNotification) {
        return true;
    }

    if (isNoResponseError(error)) {
        return true;
    }

    return isSuppressedUserErrorMessage(error.message);
};

/**
 * Resolve a user-facing error message, or null when the error should stay hidden.
 */
export const getNotifyableErrorMessage = (error, fallback) => {
    if (shouldSuppressUserError(error)) {
        return null;
    }

    if (error?.response) {
        const body = error.response.data;
        if (typeof body === 'string' && body.trim()) {
            return body.trim();
        }

        const apiResponse = parseApiResponse(error.response);
        const apiMessage = getErrorMessage(apiResponse);
        if (apiMessage && apiMessage !== 'An error occurred') {
            return apiMessage;
        }

        // Support PascalCase payloads if camelCase parsing missed fields
        if (body && typeof body === 'object') {
            const msg =
                body.responseMessage ||
                body.ResponseMessage ||
                body.responseDescription ||
                body.ResponseDescription;
            if (msg) return msg;
        }

        return fallback || error.message || 'An error occurred';
    }

    return error?.message || fallback;
};

/**
 * Show an API error toast only when it should be visible to the user.
 */
export const notifyApiError = (setError, error, fallback) => {
    const message = getNotifyableErrorMessage(error, fallback);
    if (message) {
        setError(message);
    }
};
